import { Box, Grid, MenuItem, TextField, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Link } from 'gatsby'
import * as moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppState } from '../../appState'
import bird from '../../assets/images/chirpyest-logo.svg'
import axios from '../../axios'
import { COLORS, ENDPOINTS, FONTS, ROUTES } from '../../constants'
import CHROutlinedButton from '../outlinedButton'
import NewBrandsSection from '../proTab/newBrandsSection'
import { styles } from '../proTab/styles'
import CHRSpinner from '../spinner'
import ShareLinkSortMenu, {
  DEFAULT_SHARE_LINK_SORT_STACK,
  isDefaultShareLinkColumnSorts,
  ShareLinkSortField,
  shareLinkSortStackForApi,
} from './shareLinkSortMenu'
import SortablePaginatedTable, {
  SortablePaginatedTableColumn,
} from './sortablePaginatedTable'
import TransactionSortMenu, {
  DEFAULT_TRANSACTION_SORT_STACK,
  isDefaultTransactionColumnSorts,
  TransactionSortField,
  transactionSortStackForApi,
} from './transactionSortMenu'

type TransactionSourceFilter =
  | 'all'
  | 'shopping board'
  | 'share link'
  | 'browser extension'
  | 'other'

type TransactionRetailerOption = {
  brandId: string
  brandName: string
}

type ShareLinkRow = {
  id: number | string
  productImage?: string | null
  productName?: string | null
  shortUrl?: string | null
  createdAt?: string
  clicksCount?: number | null
  earnings?: number | null
  brandName?: string | null
}

type TransactionRow = {
  id: number
  createDate: string
  brandName: string | null
  transactionAmount: number | null
  commission: number | null
  status: string | null
  source: { name: string; value?: string | null }
}

const TRANSACTION_STATUS_OPTIONS = ['closed', 'pending', 'reversed'] as const

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 250, 500] as const

const PerformanceTab = () => {
  const classes = styles()
  const [appState] = useAppState()

  const transactionsDateMin = moment()
    .subtract(2, 'years')
    .format('YYYY-MM-DD')
  const transactionsDateMax = moment().format('YYYY-MM-DD')

  const emptyTxForm = () => ({
    from: '',
    to: '',
    filterBrandIds: [] as string[],
    filterStatuses: [] as string[],
    filterSource: 'all' as TransactionSourceFilter,
    search: '',
  })

  const defaultTxApplied = () => ({
    sortStack: DEFAULT_TRANSACTION_SORT_STACK.map(s => ({ ...s })),
    ...emptyTxForm(),
  })

  const [txApplied, setTxApplied] = useState(defaultTxApplied)
  const [txForm, setTxForm] = useState(emptyTxForm)
  const [retailerOptions, setRetailerOptions] = useState<
    TransactionRetailerOption[]
  >([])
  const [retailerOptionsLoading, setRetailerOptionsLoading] = useState(false)

  const [proTransactions, setProTransactions] = useState({
    items: [] as TransactionRow[],
    loading: true,
    error: '',
    page: 1,
    totalCount: 0,
  })

  const emptyShareLinkForm = () => ({
    from: '',
    to: '',
    filterBrandIds: [] as string[],
    search: '',
  })

  const defaultShareLinkApplied = () => ({
    sortStack: DEFAULT_SHARE_LINK_SORT_STACK,
    ...emptyShareLinkForm(),
  })

  const [shareLinkApplied, setShareLinkApplied] = useState(
    defaultShareLinkApplied
  )
  const [shareLinkForm, setShareLinkForm] = useState(emptyShareLinkForm)

  const [shareLinkRetailerOptions, setShareLinkRetailerOptions] = useState<
    TransactionRetailerOption[]
  >([])
  const [
    shareLinkRetailerOptionsLoading,
    setShareLinkRetailerOptionsLoading,
  ] = useState(false)

  const [shareLinksPageSize, setShareLinksPageSize] = useState(10)
  const [transactionsPageSize, setTransactionsPageSize] = useState(10)

  const [shareLinksPerformance, setShareLinksPerformance] = useState({
    items: [] as ShareLinkRow[],
    loading: true,
    error: '',
    page: 1,
    totalCount: 0,
  })

  const theme = useTheme()
  /** Phone: tighter toolbar + stacked row cards. Tablet/desktop: multi-column tables (horizontal scroll when needed). */
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  })

  const fetchShareLinksPerformance = useCallback(
    async (page: number) => {
      setShareLinksPerformance(prev => ({
        ...prev,
        loading: true,
        error: '',
      }))
      try {
        const q = shareLinkApplied
        const params: Record<string, string | number> = {
          page,
          pageSize: shareLinksPageSize,
        }
        const sortStack = shareLinkSortStackForApi(q.sortStack)
        if (sortStack.length > 0) {
          params.sortStack = JSON.stringify(sortStack)
        }
        if (q.from) params.from = q.from
        if (q.to) params.to = q.to
        if (q.filterBrandIds.length > 0) {
          params.filterBrandIds = JSON.stringify(q.filterBrandIds)
        }
        if (q.search.trim()) params.search = q.search.trim()

        const { data } = await axios.get(
          ENDPOINTS.userShareLinks(appState.userId),
          { params }
        )
        const payload = data?.data
        const items = payload?.links ?? []
        const count = payload?.count ?? 0
        const pageReturned = payload?.page ?? page

        setShareLinksPerformance({
          items,
          loading: false,
          error: '',
          page: pageReturned,
          totalCount: count,
        })
      } catch (err) {
        setShareLinksPerformance(prev => ({
          ...prev,
          loading: false,
          error: 'Something went wrong.',
        }))
      }
    },
    [appState.userId, shareLinkApplied, shareLinksPageSize]
  )

  useEffect(() => {
    if (appState.userId) {
      fetchShareLinksPerformance(1)
    }
  }, [
    appState.userId,
    shareLinkApplied,
    shareLinksPageSize,
    fetchShareLinksPerformance,
  ])

  useEffect(() => {
    if (!appState.userId) return
    let cancelled = false
    const loadRetailers = async () => {
      setShareLinkRetailerOptionsLoading(true)
      try {
        const { data } = await axios.get(
          ENDPOINTS.userShareLinkRetailerOptions(appState.userId)
        )
        const retailers = data?.data?.retailers
        if (!cancelled && Array.isArray(retailers)) {
          setShareLinkRetailerOptions(
            retailers
              .filter(
                (r: unknown) =>
                  r != null &&
                  typeof r === 'object' &&
                  typeof (r as TransactionRetailerOption).brandId ===
                    'string' &&
                  String((r as TransactionRetailerOption).brandId).trim() !== ''
              )
              .map((r: TransactionRetailerOption) => ({
                brandId: String(r.brandId).trim(),
                brandName:
                  r.brandName != null && String(r.brandName).trim() !== ''
                    ? String(r.brandName).trim()
                    : String(r.brandId).trim(),
              }))
          )
        }
      } catch {
        if (!cancelled) setShareLinkRetailerOptions([])
      } finally {
        if (!cancelled) setShareLinkRetailerOptionsLoading(false)
      }
    }
    loadRetailers()
    return () => {
      cancelled = true
    }
  }, [appState.userId])

  const applyShareLinkFilters = () => {
    setShareLinkApplied(prev => ({
      ...prev,
      ...shareLinkForm,
    }))
  }

  const clearShareLinkFilters = () => {
    setShareLinkForm(emptyShareLinkForm())
    setShareLinkApplied(defaultShareLinkApplied())
  }

  const fetchProTransactions = useCallback(
    async (page: number) => {
      setProTransactions(prev => ({
        ...prev,
        loading: true,
        error: '',
      }))
      try {
        const q = txApplied
        const sortStack = transactionSortStackForApi(q.sortStack)
        const params: Record<string, string | number> = {
          userId: appState.userId,
          page,
          pageSize: transactionsPageSize,
        }
        if (sortStack.length > 0) {
          params.sortStack = JSON.stringify(sortStack)
        }
        if (q.from) params.from = q.from
        if (q.to) params.to = q.to
        if (q.filterBrandIds.length > 0) {
          params.filterBrandIds = JSON.stringify(q.filterBrandIds)
        }
        if (q.filterStatuses.length > 0) {
          params.filterStatuses = JSON.stringify(q.filterStatuses)
        }
        if (q.filterSource && q.filterSource !== 'all') {
          params.filterSource = q.filterSource
        }
        if (q.search.trim()) params.search = q.search.trim()

        const { data } = await axios.get(ENDPOINTS.userTransactions, {
          params,
        })
        const payload = data?.data
        const items = payload?.items ?? []
        const count = payload?.count ?? 0
        const pageReturned = payload?.page ?? page

        setProTransactions({
          items,
          loading: false,
          error: '',
          page: pageReturned,
          totalCount: count,
        })
      } catch (err) {
        setProTransactions(prev => ({
          ...prev,
          loading: false,
          error: 'Something went wrong.',
        }))
      }
    },
    [appState.userId, txApplied, transactionsPageSize]
  )

  useEffect(() => {
    if (appState.userId) {
      fetchProTransactions(1)
    }
  }, [appState.userId, txApplied, transactionsPageSize, fetchProTransactions])

  useEffect(() => {
    if (!appState.userId) return
    let cancelled = false
    const loadRetailers = async () => {
      setRetailerOptionsLoading(true)
      try {
        const { data } = await axios.get(
          ENDPOINTS.userTransactionRetailerOptions,
          {
            params: { userId: appState.userId },
          }
        )
        const retailers = data?.data?.retailers
        if (!cancelled && Array.isArray(retailers)) {
          setRetailerOptions(
            retailers
              .filter(
                (r: unknown) =>
                  r != null &&
                  typeof r === 'object' &&
                  typeof (r as TransactionRetailerOption).brandId ===
                    'string' &&
                  String((r as TransactionRetailerOption).brandId).trim() !== ''
              )
              .map((r: TransactionRetailerOption) => ({
                brandId: String(r.brandId).trim(),
                brandName:
                  r.brandName != null && String(r.brandName).trim() !== ''
                    ? String(r.brandName).trim()
                    : String(r.brandId).trim(),
              }))
          )
        }
      } catch {
        if (!cancelled) setRetailerOptions([])
      } finally {
        if (!cancelled) setRetailerOptionsLoading(false)
      }
    }
    loadRetailers()
    return () => {
      cancelled = true
    }
  }, [appState.userId])

  const applyTransactionFilters = () => {
    setTxApplied(prev => ({
      ...prev,
      ...txForm,
    }))
  }

  const clearTransactionFilters = () => {
    setTxForm(emptyTxForm())
    setTxApplied(defaultTxApplied())
  }

  const isDefaultShareLinkQuery =
    isDefaultShareLinkColumnSorts(shareLinkApplied.sortStack) &&
    !shareLinkApplied.from &&
    !shareLinkApplied.to &&
    shareLinkApplied.filterBrandIds.length === 0 &&
    !shareLinkApplied.search.trim()

  const isDefaultTxQuery =
    isDefaultTransactionColumnSorts(txApplied.sortStack) &&
    !txApplied.from &&
    !txApplied.to &&
    txApplied.filterBrandIds.length === 0 &&
    txApplied.filterStatuses.length === 0 &&
    txApplied.filterSource === 'all' &&
    !txApplied.search.trim()

  const shareLinkColumns = useMemo<
    SortablePaginatedTableColumn<ShareLinkRow, ShareLinkSortField>[]
  >(
    () => [
      {
        id: 'item',
        label: 'item',
        sortField: 'productName',
        renderCell: row => (
          <div className={classes.itemWrapper}>
            <div className={classes.circleImgWrapper}>
              <img
                src={row.productImage ? row.productImage : bird}
                className={classes.circleImg}
                alt=""
              />
            </div>
            <div className={classes.productTitleWrapper}>
              <p className={classes.productTitle}>
                {row.productName ? row.productName : 'N/A'}
              </p>
              <p className={classes.brandName}>{row.brandName}</p>
            </div>
          </div>
        ),
      },
      {
        id: 'link',
        label: 'link',
        sortField: 'shortUrl',
        renderCell: row => (
          <a
            href={`${row.shortUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.black }}
          >
            {`${row.shortUrl}`}
          </a>
        ),
      },
      {
        id: 'created',
        label: 'created',
        sortField: 'createdAt',
        renderCell: row =>
          row.createdAt ? moment(row.createdAt).format('MM/DD/YY') : '—',
      },
      {
        id: 'clicks',
        label: 'total clicks',
        sortField: 'clicksCount',
        renderCell: row => row.clicksCount,
      },
      {
        id: 'earnings',
        label: 'total earned',
        sortField: 'earnings',
        renderCell: row => `$${Number(row.earnings || 0).toFixed(2)}`,
      },
    ],
    [classes]
  )

  const transactionColumns = useMemo<
    SortablePaginatedTableColumn<TransactionRow, TransactionSortField>[]
  >(
    () => [
      {
        id: 'date',
        label: 'date',
        sortField: 'createDate',
        renderCell: row =>
          row.createDate ? moment(row.createDate).format('MM/DD/YY') : '—',
      },
      {
        id: 'retailer',
        label: 'retailer',
        sortField: 'brandName',
        renderCell: row => row.brandName || '—',
      },
      {
        id: 'amount',
        label: 'transaction amount',
        sortField: 'transactionAmount',
        renderCell: row =>
          row.transactionAmount != null
            ? `$${Number(row.transactionAmount).toFixed(2)}`
            : '—',
      },
      {
        id: 'cashBack',
        label: 'your cash back',
        sortField: 'cashBack',
        renderCell: row =>
          row.commission != null
            ? `$${Number(row.commission).toFixed(2)}`
            : '—',
      },
      {
        id: 'status',
        label: 'status',
        sortField: 'status',
        renderCell: row => row.status || '—',
      },
      {
        id: 'source',
        label: 'source',
        sortField: 'source',
        renderCell: row => (
          <>
            <Typography
              variant="subtitle1"
              component="div"
              style={{ marginBottom: 0 }}
            >
              {row.source?.name ?? '—'}
            </Typography>
            {row.source?.value ? (
              <Typography
                variant="caption"
                component="div"
                style={{ wordBreak: 'break-word' }}
              >
                {row.source.value}
              </Typography>
            ) : null}
          </>
        ),
      },
    ],
    []
  )

  return (
    <div>
      <section className={classes.midWidth}>
        <NewBrandsSection />
      </section>

      <section className={classes.midWidth}>
        <Typography variant="h1">create a “share link” performance</Typography>
        <div className={classes.transactionsToolbar}>
          <Grid
            container
            spacing={isNarrowScreen ? 1 : 2}
            className={classes.transactionsToolbarRow}
            alignItems="flex-end"
          >
            <Grid item xs={12} md={6}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                placeholder="search brand or link…"
                value={shareLinkForm.search}
                onChange={e =>
                  setShareLinkForm(prev => ({
                    ...prev,
                    search: e.target.value,
                  }))
                }
                variant="outlined"
                size="small"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    applyShareLinkFilters()
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ShareLinkSortMenu
                sort={{ sortStack: shareLinkApplied.sortStack }}
                onChange={sortStack =>
                  setShareLinkApplied(prev => ({ ...prev, sortStack }))
                }
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={isNarrowScreen ? 1 : 2}
            className={classes.transactionsToolbarRow}
          >
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                label="from"
                type="date"
                value={shareLinkForm.from}
                onChange={e =>
                  setShareLinkForm(prev => ({ ...prev, from: e.target.value }))
                }
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: transactionsDateMin,
                  max:
                    shareLinkForm.to && shareLinkForm.to <= transactionsDateMax
                      ? shareLinkForm.to
                      : transactionsDateMax,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                label="to"
                type="date"
                value={shareLinkForm.to}
                onChange={e =>
                  setShareLinkForm(prev => ({ ...prev, to: e.target.value }))
                }
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min:
                    shareLinkForm.from &&
                    shareLinkForm.from >= transactionsDateMin
                      ? shareLinkForm.from
                      : transactionsDateMin,
                  max: transactionsDateMax,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                select
                label="brand"
                value={shareLinkForm.filterBrandIds}
                onChange={e => {
                  const v = e.target.value as unknown
                  setShareLinkForm(prev => ({
                    ...prev,
                    filterBrandIds:
                      typeof v === 'string' ? (v ? [v] : []) : (v as string[]),
                  }))
                }}
                SelectProps={{
                  multiple: true,
                  disabled: shareLinkRetailerOptionsLoading,
                  renderValue: (selected: unknown) => {
                    const ids = selected as string[]
                    if (ids.length === 0) return ''
                    return ids
                      .map(
                        id =>
                          shareLinkRetailerOptions.find(r => r.brandId === id)
                            ?.brandName ?? id
                      )
                      .join(', ')
                  },
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 280 } },
                  },
                }}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText={
                  shareLinkRetailerOptionsLoading
                    ? 'loading…'
                    : shareLinkRetailerOptions.length === 0
                    ? 'no brands yet'
                    : undefined
                }
              >
                {shareLinkRetailerOptions.map(r => (
                  <MenuItem key={r.brandId} value={r.brandId}>
                    {r.brandName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <div className={classes.transactionsToolbarActions}>
            <CHROutlinedButton
              label="apply filters"
              font={FONTS.Graphik.GraphikMedium}
              onClick={applyShareLinkFilters}
            />
            <CHROutlinedButton
              label="clear filters"
              font={FONTS.Graphik.GraphikMedium}
              onClick={clearShareLinkFilters}
            />
          </div>
        </div>
        {shareLinksPerformance.error ? (
          <Box
            className={classes.sectionErrorBanner}
            role="alert"
            aria-live="polite"
          >
            <Typography component="p" className={classes.sectionErrorTitle}>
              couldn&apos;t load this section
            </Typography>
            <Typography component="p" className={classes.sectionErrorBody}>
              check your connection and try again. if it keeps happening,
              contact support.
            </Typography>
            <div className={classes.sectionErrorActions}>
              <CHROutlinedButton
                label="try again"
                font={FONTS.Graphik.GraphikMedium}
                onClick={() =>
                  fetchShareLinksPerformance(shareLinksPerformance.page || 1)
                }
              />
            </div>
          </Box>
        ) : (
          <>
            <SortablePaginatedTable<ShareLinkRow, ShareLinkSortField>
              sortMode="stack"
              sortStack={shareLinkApplied.sortStack}
              onSortChange={sortStack =>
                setShareLinkApplied(prev => ({ ...prev, sortStack }))
              }
              columns={shareLinkColumns}
              rows={shareLinksPerformance.items}
              getRowKey={row => String(row.id)}
              loading={shareLinksPerformance.loading}
              page={shareLinksPerformance.page || 1}
              pageSize={shareLinksPageSize}
              totalCount={shareLinksPerformance.totalCount}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={fetchShareLinksPerformance}
              onPageSizeChange={setShareLinksPageSize}
              minWidth={640}
              tableClassName={classes.table}
            />

            {shareLinksPerformance.loading &&
              shareLinksPerformance.items.length === 0 && (
                <Box mt={2}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="p"
                  >
                    loading share links…
                  </Typography>
                </Box>
              )}
            {shareLinksPerformance.items.length === 0 &&
              !shareLinksPerformance.loading && (
                <Box mt={2}>
                  <Typography variant="subtitle1" component="p">
                    {isDefaultShareLinkQuery
                      ? "you don't have any share links yet."
                      : 'no share links match these filters. try clearing filters or changing your search.'}
                  </Typography>
                  {isDefaultShareLinkQuery && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      component="p"
                      style={{ marginTop: 8 }}
                    >
                      create a share link to see it here:{' '}
                      <Link
                        to={ROUTES.createShareLink}
                        style={{ color: COLORS.coolTeal }}
                      >
                        share with friends
                      </Link>
                    </Typography>
                  )}
                </Box>
              )}
          </>
        )}
      </section>
      <section className={classes.midWidth}>
        <Typography variant="h1">your transactions</Typography>
        <div className={classes.transactionsToolbar}>
          <Grid
            container
            spacing={isNarrowScreen ? 1 : 2}
            className={classes.transactionsToolbarRow}
          >
            <Grid item xs={12}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                placeholder="search retailer, status, source…"
                value={txForm.search}
                onChange={e =>
                  setTxForm(prev => ({ ...prev, search: e.target.value }))
                }
                variant="outlined"
                size="small"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    applyTransactionFilters()
                  }
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            spacing={isNarrowScreen ? 1 : 2}
            className={classes.transactionsToolbarRow}
          >
            <Grid item xs={12} sm={6} md={4}>
              <TransactionSortMenu
                sort={{ sortStack: txApplied.sortStack }}
                onChange={sortStack =>
                  setTxApplied(prev => ({ ...prev, sortStack }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                label="from"
                type="date"
                value={txForm.from}
                onChange={e =>
                  setTxForm(prev => ({ ...prev, from: e.target.value }))
                }
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: transactionsDateMin,
                  max:
                    txForm.to && txForm.to <= transactionsDateMax
                      ? txForm.to
                      : transactionsDateMax,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                label="to"
                type="date"
                value={txForm.to}
                onChange={e =>
                  setTxForm(prev => ({ ...prev, to: e.target.value }))
                }
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min:
                    txForm.from && txForm.from >= transactionsDateMin
                      ? txForm.from
                      : transactionsDateMin,
                  max: transactionsDateMax,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                select
                label="retailer"
                value={txForm.filterBrandIds}
                onChange={e => {
                  const v = e.target.value as unknown
                  setTxForm(prev => ({
                    ...prev,
                    filterBrandIds:
                      typeof v === 'string' ? (v ? [v] : []) : (v as string[]),
                  }))
                }}
                SelectProps={{
                  multiple: true,
                  disabled: retailerOptionsLoading,
                  renderValue: (selected: unknown) => {
                    const ids = selected as string[]
                    if (ids.length === 0) return ''
                    return ids
                      .map(
                        id =>
                          retailerOptions.find(r => r.brandId === id)
                            ?.brandName ?? id
                      )
                      .join(', ')
                  },
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 280 } },
                  },
                }}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                helperText={
                  retailerOptionsLoading
                    ? 'loading…'
                    : retailerOptions.length === 0
                    ? 'no retailers in your history yet'
                    : undefined
                }
              >
                {retailerOptions.map(r => (
                  <MenuItem key={r.brandId} value={r.brandId}>
                    {r.brandName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                select
                label="status"
                value={txForm.filterStatuses}
                onChange={e => {
                  const v = e.target.value as unknown
                  setTxForm(prev => ({
                    ...prev,
                    filterStatuses:
                      typeof v === 'string' ? (v ? [v] : []) : (v as string[]),
                  }))
                }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected: unknown) =>
                    (selected as string[]).length === 0
                      ? ''
                      : (selected as string[]).join(', '),
                  MenuProps: {
                    PaperProps: { style: { maxHeight: 280 } },
                  },
                }}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {TRANSACTION_STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                className={classes.transactionsFilterField}
                fullWidth
                select
                label="source"
                value={txForm.filterSource ?? 'all'}
                onChange={e =>
                  setTxForm(prev => ({
                    ...prev,
                    filterSource: e.target.value as TransactionSourceFilter,
                  }))
                }
                variant="outlined"
                size="small"
              >
                <MenuItem value="all">all sources</MenuItem>
                <MenuItem value="shopping board">shopping board</MenuItem>
                <MenuItem value="share link">share link</MenuItem>
                <MenuItem value="browser extension">browser extension</MenuItem>
                <MenuItem value="other">other</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <div className={classes.transactionsToolbarActions}>
            <CHROutlinedButton
              label="apply filters"
              font={FONTS.Graphik.GraphikMedium}
              onClick={applyTransactionFilters}
            />
            <CHROutlinedButton
              label="clear filters"
              font={FONTS.Graphik.GraphikMedium}
              onClick={clearTransactionFilters}
            />
          </div>
        </div>
        {proTransactions.loading && proTransactions.items.length === 0 ? (
          <Box mt={2}>
            <CHRSpinner />
            <Typography
              variant="body2"
              color="textSecondary"
              component="p"
              style={{ marginTop: 16, textAlign: 'center' }}
            >
              loading your transactions…
            </Typography>
          </Box>
        ) : proTransactions.error ? (
          <Box
            className={classes.sectionErrorBanner}
            role="alert"
            aria-live="polite"
          >
            <Typography component="p" className={classes.sectionErrorTitle}>
              couldn&apos;t load this section
            </Typography>
            <Typography component="p" className={classes.sectionErrorBody}>
              check your connection and try again. if it keeps happening,
              contact support.
            </Typography>
            <div className={classes.sectionErrorActions}>
              <CHROutlinedButton
                label="try again"
                onClick={() => fetchProTransactions(proTransactions.page || 1)}
              />
            </div>
          </Box>
        ) : (
          <>
            {!isNarrowScreen ? (
              <SortablePaginatedTable<TransactionRow, TransactionSortField>
                sortMode="stack"
                sortStack={txApplied.sortStack}
                onSortChange={sortStack =>
                  setTxApplied(prev => ({ ...prev, sortStack }))
                }
                columns={transactionColumns}
                rows={proTransactions.items}
                getRowKey={row => `tx-${row.id}`}
                loading={proTransactions.loading}
                page={proTransactions.page || 1}
                pageSize={transactionsPageSize}
                totalCount={proTransactions.totalCount}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={fetchProTransactions}
                onPageSizeChange={setTransactionsPageSize}
                minWidth={720}
                tableClassName={classes.table}
              />
            ) : (
              <div>
                {proTransactions.items.map(row => (
                  <Grid
                    container
                    className={classes.tableCard}
                    key={`tx-m-${row.id}`}
                  >
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldName}
                      >
                        date
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldValue}
                      >
                        {row.createDate
                          ? moment(row.createDate).format('MM/DD/YY')
                          : '—'}
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldName}
                      >
                        retailer
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldValue}
                      >
                        {row.brandName || '—'}
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldName}
                      >
                        transaction amount
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldValue}
                      >
                        {row.transactionAmount != null
                          ? `$${Number(row.transactionAmount).toFixed(2)}`
                          : '—'}
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldName}
                      >
                        your cash back
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldValue}
                      >
                        {row.commission != null
                          ? `$${Number(row.commission).toFixed(2)}`
                          : '—'}
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldName}
                      >
                        status
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldValue}
                      >
                        {row.status || '—'}
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldName}
                      >
                        source
                      </Typography>
                    </Grid>
                    <Grid item sm={6} xs={6}>
                      <Typography
                        variant="subtitle1"
                        className={classes.fieldValue}
                      >
                        {row.source?.name ?? '—'}
                        {row.source?.value ? (
                          <Typography
                            variant="caption"
                            component="span"
                            display="block"
                            style={{ wordBreak: 'break-word' }}
                          >
                            {row.source.value}
                          </Typography>
                        ) : null}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </div>
            )}
            {proTransactions.items.length === 0 && !proTransactions.loading && (
              <Box mt={2}>
                <Typography variant="subtitle1" component="p">
                  {isDefaultTxQuery
                    ? 'no transactions yet'
                    : 'no transactions match these filters. try clearing filters or widening the date range.'}
                </Typography>
                {!isDefaultTxQuery && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="p"
                    style={{ marginTop: 8 }}
                  >
                    use &ldquo;clear filters&rdquo; to see all transactions in
                    the selected range.
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </section>
    </div>
  )
}

export default PerformanceTab
